import { TOOLTIP_DEFAULTS, TooltipPlacement } from './tooltip.config';
import { TooltipPosition, ViewportInfo, ElementInfo } from './tooltip.interfaces';
import { TooltipConfig } from './tooltip.config';
import { TooltipUtils } from './tooltip.utils';

// ✅ Servicio especializado en cálculos de posicionamiento
export class TooltipPositioner {
  
  /**
   * Calcula la posición óptima del tooltip con fallback inteligente
   * FIXED: Usando posicionamiento fijo respecto al viewport
   */
  static calculateOptimalPosition(
    elementInfo: ElementInfo,
    preferredPlacement: TooltipPlacement,
    config: TooltipConfig,
    viewport: ViewportInfo,
    element: HTMLElement
  ): TooltipPosition {
    const { rect, spaceAbove, spaceBelow, spaceLeft, spaceRight } = elementInfo;
    const tooltipWidth = viewport.isMobile ? config.mobileWidth : config.desktopWidth;
    const tooltipHeight = TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE;
    const isSafari = TooltipUtils.isSafari();
    
    // Debug para asegurar que tooltipWidth no es NaN
    /* if (isSafari && TooltipUtils.isDebugEnabled()) {
      console.log('[TooltipMCDT Safari Width Debug]', {
        viewport,
        config,
        tooltipWidth,
        isMobile: viewport.isMobile,
        mobileWidth: config.mobileWidth,
        desktopWidth: config.desktopWidth
      });
    } */
    
    // Fallback si tooltipWidth es NaN
    const safeTooltipWidth = isNaN(tooltipWidth) ? 250 : tooltipWidth;
    
    // Determinar posición óptima con fallback más inteligente
    let finalPlacement = preferredPlacement;
    
    // Verificar si la posición preferida tiene suficiente espacio
    const minSpaceRequired = 100; // Espacio mínimo requerido
    
    switch (preferredPlacement) {
      case 'top':
        if (spaceAbove < tooltipHeight + 20) {
          // No hay espacio arriba, probar abajo
          if (spaceBelow > tooltipHeight + 20) {
            finalPlacement = 'bottom';
          } else if (spaceRight > safeTooltipWidth + 20) {
            finalPlacement = 'right';
          } else if (spaceLeft > safeTooltipWidth + 20) {
            finalPlacement = 'left';
          }
        }
        break;
      case 'bottom':
        if (spaceBelow < tooltipHeight + 20) {
          // No hay espacio abajo, probar arriba
          if (spaceAbove > tooltipHeight + 20) {
            finalPlacement = 'top';
          } else if (spaceRight > safeTooltipWidth + 20) {
            finalPlacement = 'right';
          } else if (spaceLeft > safeTooltipWidth + 20) {
            finalPlacement = 'left';
          }
        }
        break;
      case 'left':
        if (spaceLeft < safeTooltipWidth + 20) {
          // No hay espacio a la izquierda, probar derecha
          if (spaceRight > safeTooltipWidth + 20) {
            finalPlacement = 'right';
          } else if (spaceAbove > tooltipHeight + 20) {
            finalPlacement = 'top';
          } else if (spaceBelow > tooltipHeight + 20) {
            finalPlacement = 'bottom';
          }
        }
        break;
      case 'right':
        if (spaceRight < safeTooltipWidth + 20) {
          // No hay espacio a la derecha, probar izquierda
          if (spaceLeft > safeTooltipWidth + 20) {
            finalPlacement = 'left';
          } else if (spaceAbove > tooltipHeight + 20) {
            finalPlacement = 'top';
          } else if (spaceBelow > tooltipHeight + 20) {
            finalPlacement = 'bottom';
          }
        }
        break;
    }
    
    // Debug para verificar el fallback
    /* if (isSafari && finalPlacement !== preferredPlacement && TooltipUtils.isDebugEnabled()) {
      console.log('[TooltipMCDT Smart Fallback]', {
        preferredPlacement,
        finalPlacement,
        spaces: { spaceAbove, spaceBelow, spaceLeft, spaceRight },
        tooltipDimensions: { width: safeTooltipWidth, height: tooltipHeight }
      });
    } */
    
    return this.calculateFixedPosition(rect, finalPlacement, safeTooltipWidth, config, viewport);
  }
  
  /**
   * Calcula las coordenadas exactas del tooltip usando posición fixed (respecto al viewport)
   * NUEVO MÉTODO: Para mejor control de posicionamiento
   */
  private static calculateFixedPosition(
    elementRect: DOMRect,
    placement: TooltipPlacement,
    tooltipWidth: number,
    config: TooltipConfig,
    viewport: ViewportInfo
  ): TooltipPosition {
    const margin = config.viewportMargin;
    const tooltipHeight = TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE;
    
    // Proteger contra valores NaN
    const safeRect = {
      left: isNaN(elementRect.left) ? viewport.width / 2 : elementRect.left,
      top: isNaN(elementRect.top) ? 100 : elementRect.top,
      right: isNaN(elementRect.right) ? viewport.width / 2 + 50 : elementRect.right,
      bottom: isNaN(elementRect.bottom) ? 150 : elementRect.bottom,
      width: isNaN(elementRect.width) ? 50 : elementRect.width,
      height: isNaN(elementRect.height) ? 30 : elementRect.height
    };
    
    const elementCenterX = safeRect.left + (safeRect.width / 2);
    const elementCenterY = safeRect.top + (safeRect.height / 2);
    
    let x = 0, y = 0;
    let arrowPosition: { x?: number; y?: number } = {};
    
    switch (placement) {
      case 'top':
        // Tooltip arriba del elemento
        x = Math.max(margin, Math.min(
          elementCenterX - (tooltipWidth / 2),
          viewport.width - tooltipWidth - margin
        ));
        y = safeRect.top - 10; // El tooltip se posiciona justo arriba
        arrowPosition.x = Math.max(
          config.arrowMargin,
          Math.min(elementCenterX - x, tooltipWidth - config.arrowMargin)
        );
        break;
        
      case 'bottom':
        // Tooltip debajo del elemento
        x = Math.max(margin, Math.min(
          elementCenterX - (tooltipWidth / 2),
          viewport.width - tooltipWidth - margin
        ));
        y = safeRect.bottom + 10;
        arrowPosition.x = Math.max(
          config.arrowMargin,
          Math.min(elementCenterX - x, tooltipWidth - config.arrowMargin)
        );
        break;
        
      case 'left':
        // Tooltip a la izquierda del elemento
        x = safeRect.left - 10; // Se posiciona justo a la izquierda
        y = Math.max(margin, Math.min(
          elementCenterY - (tooltipHeight / 2),
          viewport.height - tooltipHeight - margin
        ));
        arrowPosition.y = Math.max(15, Math.min(
          elementCenterY - y,
          tooltipHeight - 15
        ));
        break;
        
      case 'right':
        // Tooltip a la derecha del elemento
        x = safeRect.right + 10;
        y = Math.max(margin, Math.min(
          elementCenterY - (tooltipHeight / 2),
          viewport.height - tooltipHeight - margin
        ));
        arrowPosition.y = Math.max(15, Math.min(
          elementCenterY - y,
          tooltipHeight - 15
        ));
        break;
    }
    
    // Verificar y corregir valores NaN
    if (isNaN(x)) {
      console.warn('[TooltipMCDT] NaN detected in x position, using fallback');
      x = elementCenterX - (tooltipWidth / 2);
    }
    if (isNaN(y)) {
      console.warn('[TooltipMCDT] NaN detected in y position, using fallback');
      y = safeRect.bottom + 10;
    }
    
    // Debug para verificar el posicionamiento final
    /* if (TooltipUtils.isDebugEnabled()) {
      console.log('[TooltipMCDT Fixed Position Calculation]', {
        placement,
        elementRect: safeRect,
        calculatedPosition: { x, y },
        arrowPosition,
        viewport: { width: viewport.width, height: viewport.height },
        tooltipDimensions: { width: tooltipWidth, height: tooltipHeight }
      });
    } */
    
    return { x, y, placement, arrowPosition };
  }
  
  /**
   * Calcula las coordenadas exactas del tooltip relativo al contenedor padre
   * OPTIMIZADO: Cache del parentRect para evitar getBoundingClientRect innecesarios
   */
  private static calculateRelativePosition(
    elementRect: DOMRect,
    placement: TooltipPlacement,
    tooltipWidth: number,
    config: TooltipConfig,
    viewport: ViewportInfo,
    element: HTMLElement
  ): TooltipPosition {
    const margin = config.viewportMargin;
    
    // ✅ OPTIMIZACIÓN: Cache del parentRect para evitar llamadas innecesarias
    const parentElement = element.parentElement;
    let parentRect: DOMRect | { left: number; top: number };
    
    if (parentElement && !(parentElement as any)._tooltipParentRectCache) {
      parentRect = parentElement.getBoundingClientRect();
      // Cache temporal del rect para evitar recálculos en la misma sesión
      (parentElement as any)._tooltipParentRectCache = parentRect;
      setTimeout(() => {
        delete (parentElement as any)._tooltipParentRectCache;
      }, 100); // Cache de 100ms
    } else {
      parentRect = (parentElement as any)?._tooltipParentRectCache || { left: 0, top: 0 };
    }
    
    // Proteger contra valores NaN en Safari
    const safeRect = {
      left: isNaN(elementRect.left) ? parentRect.left + 50 : elementRect.left,
      top: isNaN(elementRect.top) ? parentRect.top + 50 : elementRect.top,
      right: isNaN(elementRect.right) ? parentRect.left + 100 : elementRect.right,
      bottom: isNaN(elementRect.bottom) ? parentRect.top + 80 : elementRect.bottom,
      width: isNaN(elementRect.width) ? 50 : elementRect.width,
      height: isNaN(elementRect.height) ? 30 : elementRect.height
    };
    
    // Calcular posiciones RELATIVAS al contenedor padre
    const elementCenterX = (safeRect.left - parentRect.left) + (safeRect.width / 2);
    const elementCenterY = (safeRect.top - parentRect.top) + (safeRect.height / 2);
    const elementRelativeLeft = safeRect.left - parentRect.left;
    const elementRelativeTop = safeRect.top - parentRect.top;
    const elementRelativeRight = elementRelativeLeft + safeRect.width;
    const elementRelativeBottom = elementRelativeTop + safeRect.height;
    
    // Debug para posicionamiento relativo - SOLO cuando debug está habilitado
    const isSafari = TooltipUtils.isSafari();
    /* if (isSafari && TooltipUtils.isDebugEnabled()) {
      console.log('[TooltipMCDT Safari Relative Position Debug]', {
        elementRect,
        parentRect,
        safeRect,
        tooltipWidth,
        elementRelativePosition: {
          left: elementRelativeLeft,
          top: elementRelativeTop,
          centerX: elementCenterX,
          centerY: elementCenterY
        },
        viewport
      });
    } */
    
    let x = 0, y = 0;
    let arrowPosition: { x?: number; y?: number } = {};
    const tooltipHeight = TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE;
    
    switch (placement) {
      case 'top':
        x = elementCenterX - (tooltipWidth / 2);
        y = elementRelativeTop - tooltipHeight - 10;
        arrowPosition.x = Math.max(
          config.arrowMargin,
          Math.min(elementCenterX - x, tooltipWidth - config.arrowMargin)
        );
        break;
        
      case 'bottom':
        x = elementCenterX - (tooltipWidth / 2);
        y = elementRelativeBottom + 10;
        arrowPosition.x = Math.max(
          config.arrowMargin,
          Math.min(elementCenterX - x, tooltipWidth - config.arrowMargin)
        );
        break;
        
      case 'left':
        x = elementRelativeLeft - tooltipWidth - 15;
        y = elementCenterY - (tooltipHeight / 2);
        arrowPosition.y = Math.max(15, Math.min(
          elementCenterY - y,
          tooltipHeight - 15
        ));
        break;
        
      case 'right':
        x = elementRelativeRight + 15;
        y = elementCenterY - (tooltipHeight / 2);
        arrowPosition.y = Math.max(15, Math.min(
          elementCenterY - y,
          tooltipHeight - 15
        ));
        break;
    }

    // ✅ VIEWPORT BOUNDARY DETECTION - Corregir posiciones que se salen de la pantalla
    
    // Ajustar X si se sale por los lados (especialmente importante en mobile)
    const absoluteX = parentRect.left + x;
    const absoluteRight = absoluteX + tooltipWidth;
    
    if (absoluteX < margin) {
      // Se sale por la izquierda
      x = margin - parentRect.left;
    } else if (absoluteRight > viewport.width - margin) {
      // Se sale por la derecha
      x = (viewport.width - margin - tooltipWidth) - parentRect.left;
    }
    
    // Ajustar Y si se sale por arriba o abajo
    const absoluteY = parentRect.top + y;
    const absoluteBottom = absoluteY + tooltipHeight;
    
    if (absoluteY < margin) {
      // Se sale por arriba
      y = margin - parentRect.top;
    } else if (absoluteBottom > viewport.height - margin) {
      // Se sale por abajo
      y = (viewport.height - margin - tooltipHeight) - parentRect.top;
    }
    
    // Debug para mobile viewport corrections - OPTIMIZADO
    /* if ((viewport.isMobile || isSafari) && TooltipUtils.isDebugEnabled()) {
      console.log('[TooltipMCDT Mobile Viewport Correction]', {
        placement,
        originalPosition: { x: elementCenterX - (tooltipWidth / 2), y: elementRelativeBottom + 10 },
        correctedPosition: { x, y },
        parentRect,
        viewport: { width: viewport.width, height: viewport.height },
        tooltipDimensions: { width: tooltipWidth, height: tooltipHeight },
        boundaries: {
          absoluteX,
          absoluteRight,
          absoluteY,
          absoluteBottom
        }
      });
    } */
    
    // Verificar y corregir valores NaN
    if (isNaN(x)) {
      console.warn('[TooltipMCDT] NaN detected in relative x position, using fallback');
      x = elementRelativeLeft;
    }
    if (isNaN(y)) {
      console.warn('[TooltipMCDT] NaN detected in relative y position, using fallback');
      y = elementRelativeBottom + 10;
    }
    
    // ✅ RECALCULAR FLECHA después de viewport corrections
    if (placement === 'top' || placement === 'bottom') {
      // Para tooltips horizontales, ajustar flecha X
      arrowPosition.x = Math.max(
        config.arrowMargin,
        Math.min(elementCenterX - x, tooltipWidth - config.arrowMargin)
      );
    } else {
      // Para tooltips verticales, ajustar flecha Y  
      arrowPosition.y = Math.max(15, Math.min(
        elementCenterY - y,
        tooltipHeight - 15
      ));
    }
    
    /* if (isSafari && TooltipUtils.isDebugEnabled()) {
      console.log('[TooltipMCDT Safari Final Relative Position]', {
        placement,
        calculatedPosition: { x, y },
        arrowPosition,
        hasNaN: {
          x: isNaN(x),
          y: isNaN(y)
        }
      });
    } */
    
    return { x, y, placement, arrowPosition };
  }

  /**
   * MÉTODO ANTERIOR - Calcula las coordenadas exactas del tooltip y su flecha
   * Con corrección específica para Safari móvil (PARA REFERENCIA)
   */
  private static calculatePosition(
    elementRect: DOMRect,
    placement: TooltipPlacement,
    tooltipWidth: number,
    config: TooltipConfig,
    viewport: ViewportInfo
  ): TooltipPosition {
    const margin = config.viewportMargin;
    
    // Proteger contra valores NaN en Safari
    const safeRect = {
      left: isNaN(elementRect.left) ? viewport.width / 2 : elementRect.left,
      top: isNaN(elementRect.top) ? 100 : elementRect.top,
      right: isNaN(elementRect.right) ? viewport.width / 2 + 50 : elementRect.right,
      bottom: isNaN(elementRect.bottom) ? 150 : elementRect.bottom,
      width: isNaN(elementRect.width) ? 50 : elementRect.width,
      height: isNaN(elementRect.height) ? 30 : elementRect.height
    };
    
    const elementCenterX = safeRect.left + (safeRect.width / 2);
    const elementCenterY = safeRect.top + (safeRect.height / 2);
    
    // Safari a veces tiene un pequeño desplazamiento en el cálculo, aplicamos corrección mínima
    const isSafari = TooltipUtils.isSafari();
    const safariCorrection = 0; // ✅ Eliminamos corrección por ahora para probar
    
    // Debug para valores antes del cálculo
    /* if (isSafari) {
      console.log('[TooltipMCDT Safari Pre-Calculation Debug]', {
        originalRect: elementRect,
        safeRect,
        tooltipWidth,
        elementCenterX,
        elementCenterY,
        margin,
        viewport,
        hasNaN: {
          left: isNaN(elementRect.left),
          top: isNaN(elementRect.top),
          width: isNaN(elementRect.width),
          height: isNaN(elementRect.height)
        }
      });
    } */
    
    let x = 0, y = 0;
    let arrowPosition: { x?: number; y?: number } = {};
    
    switch (placement) {
      case 'top':
        x = Math.max(margin, Math.min(
          elementCenterX - (tooltipWidth / 2),
          viewport.width - tooltipWidth - margin
        ));
        y = safeRect.top - 10 - safariCorrection;
        arrowPosition.x = Math.max(
          config.arrowMargin,
          Math.min(elementCenterX - x, tooltipWidth - config.arrowMargin)
        );
        break;
        
      case 'bottom':
        x = Math.max(margin, Math.min(
          elementCenterX - (tooltipWidth / 2),
          viewport.width - tooltipWidth - margin
        ));
        y = safeRect.bottom + 10 + safariCorrection;
        arrowPosition.x = Math.max(
          config.arrowMargin,
          Math.min(elementCenterX - x, tooltipWidth - config.arrowMargin)
        );
        break;
        
      case 'left':
        x = safeRect.left - tooltipWidth - 15 - safariCorrection;
        y = Math.max(margin, Math.min(
          elementCenterY - (TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE / 2),
          viewport.height - TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE - margin
        ));
        arrowPosition.y = Math.max(15, Math.min(
          elementCenterY - y,
          TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE - 15
        ));
        break;
        
      case 'right':
        x = safeRect.right + 15 + safariCorrection;
        y = Math.max(margin, Math.min(
          elementCenterY - (TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE / 2),
          viewport.height - TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE - margin
        ));
        arrowPosition.y = Math.max(15, Math.min(
          elementCenterY - y,
          TOOLTIP_DEFAULTS.TOOLTIP_HEIGHT_ESTIMATE - 15
        ));
        break;
    }
    
    // Verificar y corregir NaN values
    if (isNaN(x)) {
      console.warn('[TooltipMCDT] NaN detected in x position, using fallback');
      x = elementRect.left;
    }
    if (isNaN(y)) {
      console.warn('[TooltipMCDT] NaN detected in y position, using fallback');
      y = elementRect.bottom + 10;
    }
    if (arrowPosition.x && isNaN(arrowPosition.x)) {
      console.warn('[TooltipMCDT] NaN detected in arrow x position, using fallback');
      arrowPosition.x = tooltipWidth / 2;
    }
    if (arrowPosition.y && isNaN(arrowPosition.y)) {
      console.warn('[TooltipMCDT] NaN detected in arrow y position, using fallback');
      arrowPosition.y = 15;
    }
    
    // Debug solo para casos problemáticos - mejorado
    /* if (isSafari) {
      console.log('[TooltipMCDT Safari Position Debug]', {
        placement,
        elementRect: { 
          top: safeRect.top, 
          left: safeRect.left, 
          bottom: safeRect.bottom,
          right: safeRect.right,
          width: safeRect.width, 
          height: safeRect.height 
        },
        calculatedPosition: { x, y },
        safariCorrection,
        arrowPosition,
        viewport: { width: viewport.width, height: viewport.height },
        // Información adicional para diagnosticar
        elementPosition: {
          centerX: elementCenterX,
          centerY: elementCenterY
        }
      });
    } */
    
    return { x, y, placement, arrowPosition };
  }
}
